/* eslint-disable camelcase */
// Detailed run history for one client advertising report, reached from that
// schedule's row in the unified table. Statuses are never overstated: a
// generated file is not evidence that an email was delivered.
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography,
    alpha,
} from '@mui/material';
import ReportDataGrid from 'src/components/table/ReportDataGrid';
import { Iconify } from 'src/components';
import { statusLabel } from '../clientReports/clientReportModel';
import ClientReportPreview from '../clientReports/ClientReportPreview';
import { RUN_HISTORY_GRID_DEFAULTS } from './scheduleGridDefaults';
import { NOT_AVAILABLE, formatUkDateTime } from './reportSchedulePresentation';
import { reportingFocusVisibleSx } from './focusVisible';
import EmailText from './EmailText';

const WARNING_STATUSES = ['blocked', 'generation_failed', 'delivery_failed', 'ambiguous'];

const runTypeLabel = (row) => {
    if (row.is_test) return 'Test';
    return row.schedule_id ? 'Scheduled' : 'Download';
};

export default function ClientRunHistoryDialog({ open, schedule, state, onClose }) {
    const scheduleId = schedule?.id ?? null;

    // Narrow the shared history resource to this schedule while the dialog is
    // open, and release it on close so the page-level history is unaffected.
    useEffect(() => {
        if (!open) return undefined;
        state.setHistoryQuery((previous) => ({ ...previous, page: 0, scheduleId }));
        return () => state.setHistoryQuery((previous) => ({ ...previous, page: 0, scheduleId: null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, scheduleId]);

    const columns = [
        {
            field: 'title',
            headerName: 'Run',
            flex: 1,
            minWidth: 200,
            renderCell: ({ row }) => <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{row.title}</Typography>,
        },
        {
            field: 'is_test',
            headerName: 'Run type',
            width: 130,
            valueGetter: ({ row }) => runTypeLabel(row),
            renderCell: ({ row }) => <Chip size="small" label={runTypeLabel(row)} />,
        },
        {
            field: 'period',
            headerName: 'Reporting period',
            width: 220,
            valueGetter: ({ row }) => row.period?.label || NOT_AVAILABLE,
        },
        {
            field: 'recipients',
            headerName: 'Recipients',
            width: 220,
            valueGetter: ({ row }) => (row.recipients || []).join(', ') || 'No email',
            renderCell: ({ row }) => (
                <EmailText noWrap sx={{ fontSize: '13px' }}>
                    {(row.recipients || []).join(', ') || 'No email'}
                </EmailText>
            ),
        },
        {
            field: 'status',
            headerName: 'Result',
            width: 260,
            valueGetter: ({ row }) => statusLabel(row.status),
        },
        {
            field: 'created_at',
            headerName: 'Created',
            width: 180,
            valueGetter: ({ row }) => formatUkDateTime(row.created_at),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 190,
            sortable: false,
            disableExport: true,
            renderCell: ({ row }) => (
                <Stack direction="row" spacing={0.5}>
                    <Button size="small" disabled={state.busy} onClick={() => state.inspect(row)} sx={{ borderRadius: '8px' }}>
                        View
                    </Button>
                    {row.status === 'generation_failed' && (
                        <Button size="small" disabled={state.busy} onClick={() => state.retry(row)}>
                            Retry
                        </Button>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <Dialog open={open} onClose={state.busy ? undefined : onClose} maxWidth="lg" fullWidth
            slotProps={{ paper: { sx: { ...reportingFocusVisibleSx, borderRadius: '16px' } } }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '10px',
                    bgcolor: alpha('#f59e0b', 0.12),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Iconify icon="mdi:history" width={22} sx={{ color: '#f59e0b' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Run history</Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                        {schedule?.title || 'Client advertising report'} — generated files are not evidence of email
                        delivery. Provider acceptance and confirmed delivery are shown separately.
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Close run history">
                    <Iconify icon="eva:close-fill" width={20} />
                </IconButton>
            </Box>
            <DialogContent sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                    {state.historyError && (
                        <Alert
                            severity="error"
                            action={
                                <Button onClick={state.reloadHistory} disabled={state.busy}>
                                    Refresh
                                </Button>
                            }
                        >
                            {state.historyError}
                        </Alert>
                    )}
                    <ReportDataGrid
                        tableName="clientReportRunHistory"
                        columns={columns}
                        defaults={RUN_HISTORY_GRID_DEFAULTS}
                        rows={state.history.results}
                        rowCount={state.history.count}
                        loading={state.historyLoading}
                        paginationMode="server"
                        queryResetKey={String(scheduleId || 'none')}
                        onQueryStateChange={({ paginationModel }) =>
                            state.setHistoryQuery((previous) =>
                                previous.page === paginationModel.page && previous.pageSize === paginationModel.pageSize
                                    ? previous
                                    : { ...previous, page: paginationModel.page, pageSize: paginationModel.pageSize }
                            )
                        }
                        csvFileName="client-report-runs"
                        autoHeight
                    />
                    {!state.historyLoading && !state.historyError && !state.history.count && (
                        <Typography sx={{ color: '#64748b' }}>No report runs yet.</Typography>
                    )}
                    {state.error && <Alert severity="error">{state.error}</Alert>}
                    {state.run && (
                        <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#f8fafc' }}>
                            <Stack spacing={1.5}>
                                <Alert severity={WARNING_STATUSES.includes(state.run.status) ? 'warning' : 'info'}>
                                    {state.run.title} · {statusLabel(state.run.status)}
                                    {state.run.error_code ? ` (${state.run.error_code})` : ''}
                                </Alert>
                                <Typography sx={{ fontSize: '13px' }}>
                                    {state.run.is_test ? 'Test' : 'Report'} · Recipients:{' '}
                                    <EmailText sx={{ fontSize: '13px' }}>
                                        {state.run.recipients?.join(', ') || 'No email'}
                                    </EmailText>
                                </Typography>
                                <ClientReportPreview snapshot={state.run.snapshot} />
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                    {state.run.artifacts?.map((artifact) => (
                                        <Button
                                            key={artifact.id}
                                            variant="outlined"
                                            size="small"
                                            startIcon={<Iconify icon="mdi:download" width={16} />}
                                            disabled={state.busy}
                                            onClick={() => state.download(state.run, artifact)}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            Download {artifact.format}
                                        </Button>
                                    ))}
                                    {state.run.status === 'generation_failed' && (
                                        <Button size="small" disabled={state.busy} onClick={() => state.retry(state.run)}>
                                            Retry generation
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
}

ClientRunHistoryDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    schedule: PropTypes.object,
    state: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
};
